import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { fileUrl } from "@/lib/api";
import { Flame, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";

export default function FeaturedHeroCarousel({ featured = [] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);

  // Bounds safety
  const total = featured.length;
  const activeIndex = total > 0 ? (currentIndex % total + total) % total : 0;
  const current = total > 0 ? featured[activeIndex] : null;

  const handleNext = useCallback(() => {
    if (total <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % total);
  }, [total]);

  const handlePrev = useCallback(() => {
    if (total <= 1) return;
    setCurrentIndex((prev) => (prev - 1 + total) % total);
  }, [total]);

  // Auto-rotate every 5 seconds, paused when hovered/focused
  useEffect(() => {
    if (total <= 1 || isPaused) return;

    timerRef.current = setInterval(() => {
      handleNext();
    }, 5000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [total, isPaused, handleNext]);

  if (!current) return null;

  const cleanDesc = (current.description || "")
    .replace(/<[^>]+>/g, "")
    .slice(0, 160);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="mt-14 grid lg:grid-cols-12 gap-6 items-stretch"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="featured-hero-carousel"
    >
      {/* Big Hero Card (Col 8) */}
      <div className="lg:col-span-8 relative group rounded-2xl overflow-hidden border border-white/10 glow-crimson bg-zinc-950 min-h-[420px] sm:min-h-[440px] flex flex-col justify-end">
        {/* Clickable Area for the whole card to open the post */}
        <Link
          to={`/posts/${current.slug}`}
          className="absolute inset-0 z-0 block cursor-pointer"
          data-testid={`featured-hero-${current.slug}`}
          title={`Buka ${current.title}`}
        >
          {/* Animated Background Image */}
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id || activeIndex}
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="absolute inset-0"
            >
              {current.thumbnail_url ? (
                <img
                  src={fileUrl(current.thumbnail_url)}
                  alt={current.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-rose-950 via-zinc-900 to-black" />
              )}
            </motion.div>
          </AnimatePresence>

          {/* Vignette & Gradients */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent hidden sm:block" />
        </Link>

        {/* Content on Bottom Left */}
        <div className="relative z-10 p-6 sm:p-8 pointer-events-none max-w-2xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id || activeIndex}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35 }}
            >
              {/* Badges */}
              <div className="flex flex-wrap items-center gap-2 mb-3 pointer-events-auto">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-600/40 border border-rose-500/60 shadow-[0_0_12px_rgba(225,29,72,0.4)] text-xs text-rose-200 font-semibold tracking-wide">
                  <Flame className="w-3.5 h-3.5 text-rose-300 animate-pulse" />
                  Featured TL
                </span>
                {current.version && (
                  <span className="px-2.5 py-0.5 rounded-full bg-white/10 border border-white/15 text-xs text-zinc-300 font-mono">
                    v{current.version}
                  </span>
                )}
                {current.category && (
                  <span className="px-2.5 py-0.5 rounded-full bg-rose-950/60 border border-rose-800/40 text-[11px] text-rose-300 capitalize">
                    {current.category}
                  </span>
                )}
              </div>

              {/* Title */}
              <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black text-white leading-tight drop-shadow-md">
                <Link
                  to={`/posts/${current.slug}`}
                  className="pointer-events-auto hover:text-rose-400 transition"
                >
                  {current.title}
                </Link>
              </h3>

              {/* Description */}
              {cleanDesc && (
                <p className="text-zinc-300 text-sm sm:text-base mt-2 line-clamp-2 drop-shadow">
                  {cleanDesc}
                </p>
              )}

              {/* CTA button */}
              <div className="mt-5 flex items-center gap-3 pointer-events-auto">
                <Link
                  to={`/posts/${current.slug}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-semibold text-sm shadow-[0_0_20px_rgba(225,29,72,0.5)] transition group/btn"
                >
                  <span>Unseal TL Post</span>
                  <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                </Link>

                <div className="text-xs text-zinc-400 font-mystic hidden sm:block">
                  Auto-slide {isPaused ? "(Dijeda)" : "5s"}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation Arrows < and > on the Big Hero Card */}
        {total > 1 && (
          <>
            {/* Left Button < */}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handlePrev();
              }}
              aria-label="Previous Featured Game"
              title="Game Sebelumnya (<)"
              className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full glass border border-white/20 text-white flex items-center justify-center hover:bg-rose-600 hover:border-rose-400 hover:scale-110 active:scale-95 transition-all shadow-xl backdrop-blur-md opacity-85 group-hover:opacity-100"
              data-testid="hero-carousel-prev"
            >
              <ChevronLeft className="w-6 h-6 -ml-0.5 text-white drop-shadow" />
            </button>

            {/* Right Button > */}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleNext();
              }}
              aria-label="Next Featured Game"
              title="Game Selanjutnya (>)"
              className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full glass border border-white/20 text-white flex items-center justify-center hover:bg-rose-600 hover:border-rose-400 hover:scale-110 active:scale-95 transition-all shadow-xl backdrop-blur-md opacity-85 group-hover:opacity-100"
              data-testid="hero-carousel-next"
            >
              <ChevronRight className="w-6 h-6 -mr-0.5 text-white drop-shadow" />
            </button>

            {/* Progress Pills / Dots Indicators at Bottom Right */}
            <div className="absolute bottom-4 right-4 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10">
              {featured.map((item, idx) => (
                <button
                  key={item.id || idx}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCurrentIndex(idx);
                  }}
                  aria-label={`Pilih slide ${idx + 1}`}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    idx === activeIndex
                      ? "w-7 bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.8)]"
                      : "w-2 bg-white/30 hover:bg-white/60"
                  }`}
                />
              ))}
              <span className="text-[11px] text-zinc-400 ml-1 font-mono">
                {activeIndex + 1}/{total}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Mini Cards Column (Col 4) */}
      <div className="lg:col-span-4 flex flex-col gap-3 justify-between">
        <div className="flex items-center justify-between px-1 mb-1">
          <div className="text-xs uppercase tracking-widest text-zinc-400 font-semibold flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
            Sorotan Game Pilihan
          </div>
          <span className="text-xs text-zinc-500 font-mono">
            {total} Game
          </span>
        </div>

        <div className="grid grid-cols-1 gap-2.5 flex-1">
          {featured.slice(0, 4).map((p, idx) => {
            const isActive = idx === activeIndex;
            return (
              <div
                key={p.id}
                onClick={() => setCurrentIndex(idx)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setCurrentIndex(idx);
                  }
                }}
                className={`group relative flex gap-3 p-2.5 rounded-xl transition cursor-pointer text-left select-none ${
                  isActive
                    ? "bg-rose-950/40 border-2 border-rose-500 shadow-[0_0_20px_rgba(244,63,94,0.25)] ring-1 ring-rose-500/50"
                    : "glass border border-white/10 hover:border-white/20 hover:bg-white/[0.04]"
                }`}
                data-testid={`featured-mini-${p.slug}`}
              >
                {/* Thumbnail */}
                <div className="relative w-24 h-20 rounded-lg overflow-hidden bg-zinc-900 flex-shrink-0 border border-white/10">
                  {p.thumbnail_url ? (
                    <img
                      src={fileUrl(p.thumbnail_url)}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full bg-zinc-800" />
                  )}
                  {isActive && (
                    <div className="absolute inset-0 border-2 border-rose-500 rounded-lg pointer-events-none" />
                  )}
                </div>

                {/* Info */}
                <div className="min-w-0 flex-1 flex flex-col justify-center">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    {isActive ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-400 bg-rose-500/20 px-1.5 py-0.5 rounded border border-rose-500/30">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-ping" />
                        Sedang Ditampilkan
                      </span>
                    ) : (
                      <span className="text-[11px] text-rose-400 font-semibold uppercase tracking-wider">
                        v{p.version || "—"}
                      </span>
                    )}
                  </div>
                  <div
                    className={`font-display text-sm font-bold truncate transition ${
                      isActive ? "text-white" : "text-zinc-200 group-hover:text-white"
                    }`}
                  >
                    {p.title}
                  </div>
                  <div className="text-[11px] text-zinc-400 mt-0.5 truncate">
                    {(p.tags || []).slice(0, 2).join(" · ") || p.category || "Visual Novel"}
                  </div>
                </div>

                {/* Direct link arrow icon */}
                <div className="flex items-center pr-1 text-zinc-500 group-hover:text-rose-400 transition">
                  <ArrowRight className="w-4 h-4 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}
